import { Component } from 'react';
import './Menu.scss';
import { CssBaseline } from "@material-ui/core";
import i18n from "../i18n";

import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Avatar from '@material-ui/core/Avatar';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import CancelIcon from "@material-ui/icons/Cancel";
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import ShareOutlinedIcon from '@material-ui/icons/ShareOutlined';
import TranslateIcon from '@material-ui/icons/Translate';
import SvgIcon from '@material-ui/core/SvgIcon';
import IconReportError from '../icons/icon_report_error_current.svg?react';

import SiteAuth from "../common/SiteAuth";
import Typography from '@material-ui/core/Typography';
import Config from "../common/Config";
import AppService from "../common/AppService";
import GronzePageService from "../common/GronzePageService";
import About from "./pages/About";
import DataService from "../common/DataService";
import { getExternalPages } from "../common/ExternalPages";
import ExternalPage from "./pages/ExternalPage";
import { BuildOutlined, InfoOutlined, StarOutlined, SystemUpdate } from '@material-ui/icons';
import log from "loglevel";
import UrlService from "../common/UrlService";
import AnalyticsAPIService from "../common/AnalyticsAPIService";
import { Share } from "@capacitor/share";
import UpdateService from "../common/UpdateService";
import { ModalComponentService } from "../common/ModalComponentService";
import RateAppService from "../common/RateAppService";
import AppStorage from "../storage/AppStorage";
import { LocaleService } from "../common/LocaleService";
import NetworkService from "../common/NetworkService";
import LogManager from "../common/LogManager";
import type { ExternalPageName } from "../common/ExternalPages";

export interface MenuProps {
    /** Cierra la sesion. Lo pasa App. */
    unautorize: () => void;
    /**
     * True si el backend publica una version mayor que la instalada. Lo
     * comprueba App, que ya marca con ello el boton de menu.
     * @see App.checkAppUpdate
     */
    updateAvailable: boolean;
}

interface MenuState {
    userName: string;
    userPicture: string | undefined;
    userId: string | number;
    showUser: boolean;
    showLogout: boolean;
}

class Menu extends Component<MenuProps, MenuState> {

    constructor(props: MenuProps) {
        super(props);

        this.state = {
            userName: '',
            userPicture: '',
            userId: '',
            showUser: AppService.showUserAccount(),
            showLogout: SiteAuth.canLogout(),
        };
        this.initComponent();

        this.close = this.close.bind(this);
        this.closeSession = this.closeSession.bind(this);
    }

    async initComponent(): Promise<void> {
        if (!this.state.showUser) {
            return;
        }
        const userData = await SiteAuth.getUserData();

        this.setState({
            userName: userData!.getName(),
            userPicture: userData!.getPictureUrl(),
            userId: userData!.getUserId(),
        });
    }

    close(): void {
        AppService.appcomponent.closeMenu(false);
    }

    async openExternalHTML(pageName: ExternalPageName, label: string): Promise<void> {
        if (NetworkService.isOffline()) {
            return;
        }

        try {
            const pageId = getExternalPages()[pageName];
            const pageHtml = await DataService.getPageById(pageId, label);
            
            if (!pageHtml) {
                return;
            }

            GronzePageService.componentOpenHandler(
                <ExternalPage pageHtml={pageHtml.contentHtml} title={pageHtml.title} />
            );
        } catch (error) {
            log.error('Menu.openExternalHTML: error opening external page', error);
            LogManager.createError(i18n.t('offline.message'));
        }
    }

    openGronzePage(): void {
        GronzePageService.componentOpenHandler(<About />);
    }

    shareApp(): void {
        const domain = Config.getRestUrl();
        Share.share({
            text: i18n.t('share.app_text'),
            url: `${domain}/gronze-maps`,
        })
            .catch(() => {
                log.debug('Error compartiendo app o compartir cancelado');
            });
    }

    closeSession(): void {
        if (!this.state.showLogout) {
            return;
        }
        this.props.unautorize();
    }

    openSlider(): void {
        AppStorage.setOpenedBefore(true);
        ModalComponentService
            .openModalComponent(ModalComponentService.ModalComponentTypes.INITIAL_CARD);
    }

    openSupportForm(): void {
        const lang = LocaleService.getLanguage();
        const path = lang === 'es' ? '/formulario/gronze-maps' : '/en/form/gronze-maps';
        const url = Config.getSiteUrl() + path;
        const params = new URLSearchParams();
        params.set('version', Config.getAppVersion());
        params.set('status', AppService.getDebugInfo());
        params.set('device', AppService.deviceInfo.model);
        params.set('os', AppService.deviceInfo.platform);
        params.set('operatingSystem', AppService.deviceInfo.operatingSystem);
        params.set('osVersion', AppService.deviceInfo.osVersion);
        params.set('manufacturer', AppService.deviceInfo.manufacturer);
        log.debug('Menu.formulario/gronze-maps', url);
        const finalUrl = url + '?' + params.toString();
        UrlService.openInfo(finalUrl);
        AnalyticsAPIService.sendOutboundLink(
            i18n.t('menu.go_support'),
            url
        );
    }

    openReportErrorForm(): void {
        // Opens the report error modal instead of navigating to an external form.
        ModalComponentService
            .openModalComponent(ModalComponentService.ModalComponentTypes.REPORT_ERROR_CARD);
    }

    updateApp(): void {
        if (AppService.isNativeApp()) {
            UpdateService.goToStore(AppService.isIos());
        }
    }

    rateApp(): void {
        if (AppService.isNativeApp()) {
            RateAppService.rateApp();
        }
    }

    selectLanguage(): void {
        ModalComponentService
            .openModalComponent(ModalComponentService.ModalComponentTypes.LANGUAGE_SELECTOR_CARD);
    }

    render() {
        // <Avatar src="/assets/logo-gronze.svg" alt="Logo Gronze.com" variant="square" />

        return (
            <div className="menu">
                <CssBaseline />
                <Typography className="title" variant="h4" component="h2">
                    {Config.getAppName()}
                </Typography>
                <List>
                    {
                        this.state.showUser ?
                            <div><ListItem>
                                <ListItemAvatar>
                                    <Avatar alt={'Foto de ' + this.state.userName} src={this.state.userPicture} />
                                </ListItemAvatar>
                                <ListItemText primary={this.state.userName} />
                            </ListItem>
                                <Divider /></div>
                            :
                            null
                    }

                    <ListItem button component="a"
                        onClick={() => {
                            this.openGronzePage();
                        }}
                    >
                        <ListItemIcon><InfoOutlined /></ListItemIcon>
                        <ListItemText primary={i18n.t('help.about_link')} />
                    </ListItem>

                    {
                        AppService.canShare ?
                            <ListItem button component="a"
                                onClick={() => {
                                    this.shareApp();
                                }}
                            >
                                <ListItemIcon><ShareOutlinedIcon /></ListItemIcon>
                                <ListItemText primary={i18n.t('share.app_link')} />
                            </ListItem>
                            :
                            null
                    }



                    {
                        AppService.isNativeApp() ?
                            <ListItem button component="a"
                                onClick={this.rateApp}
                            >
                                <ListItemIcon>
                                    <StarOutlined />
                                </ListItemIcon>
                                <ListItemText primary={i18n.t('menu.rate')} />
                            </ListItem>
                            :
                            null
                    }

                    <ListItem button component="a"
                        onClick={() => {
                            this.openSlider();
                        }}
                    >
                        <ListItemIcon><HelpOutlineIcon /></ListItemIcon>
                        <ListItemText primary={i18n.t('help.help_link')} />
                    </ListItem>

                    <ListItem button
                        onClick={() => {
                            this.openExternalHTML(
                                'legal_usage',
                                i18n.t('legalConditions.link_usage'),
                            )
                        }}
                    >
                        <ListItemIcon><NavigateNextIcon /></ListItemIcon>
                        <ListItemText primary={i18n.t('legalConditions.link_usage')} />
                    </ListItem>
                    <ListItem button
                        onClick={() => {
                            this.openExternalHTML(
                                'legal_privacy',
                                i18n.t('legalConditions.link_private'),
                            )
                        }}
                    >
                        <ListItemIcon><NavigateNextIcon /></ListItemIcon>
                        <ListItemText primary={i18n.t('legalConditions.link_private')} />
                    </ListItem>

                    {
                        AppService.isPwa() ?
                            <ListItem button
                                onClick={() => {
                                    this.openExternalHTML(
                                        'legal_cookies',
                                        i18n.t('legalConditions.link_cookies'),
                                    )
                                }}
                            >
                                <ListItemIcon><NavigateNextIcon /></ListItemIcon>
                                <ListItemText primary={i18n.t('legalConditions.link_cookies')} />
                            </ListItem>
                            :
                            null
                    }

                    <Divider />

                    <ListItem button component="a"
                        onClick={() => { this.openReportErrorForm() }}
                    >
                        <ListItemIcon>
                            <SvgIcon component={IconReportError} viewBox="0 0 24 24" />
                        </ListItemIcon>
                        <ListItemText primary={i18n.t('menu.error')} />
                    </ListItem>

                    <ListItem button component="a"
                        onClick={() => { this.openSupportForm() }}
                    >
                        <ListItemIcon><BuildOutlined /></ListItemIcon>
                        <ListItemText primary={i18n.t('menu.go_support')} />
                    </ListItem>

                    {
                        this.props.updateAvailable ?
                            <ListItem button component="a" className="update-element"
                                onClick={this.updateApp}
                            >
                                <ListItemIcon>
                                    <SystemUpdate className="update-icon" />
                                </ListItemIcon>
                                <ListItemText primary={i18n.t('menu.update')} />
                            </ListItem>
                            :
                            null
                    }

                    <Divider />

                    <ListItem button
                        onClick={this.selectLanguage}
                    >
                        <ListItemIcon><TranslateIcon /></ListItemIcon>
                        <ListItemText primary={i18n.t('menu.language')} />
                    </ListItem>

                    {
                        this.state.showLogout ?
                            <ListItem button onClick={this.closeSession} >
                                <ListItemIcon><ExitToAppIcon /></ListItemIcon>
                                <ListItemText primary={i18n.t('menu.close_session')} />
                            </ListItem>
                            :
                            null
                    }

                    <ListItem >
                        <ListItemText className="brand" secondary={AppService.getAppInfoLabel()} />
                    </ListItem>
                </List>

                <div className="cancel-icon">
                    <CancelIcon color="action" onClick={this.close} />
                </div>
            </div>
        );
    }
}

export default Menu;

